/**
 * Integrytas Group - User-Defined Column Engine (UDC)
 * UdcCustomFields - drop-in renderer for user-defined columns.
 *
 * Place this anywhere in a screen's layout (per tab/section). It hydrates the
 * active-workspace custom fields for `entity`, renders each as a native control
 * (derived from the DSE data_type or a layout override), and binds to the parent
 * form's ext_attributes object via value/onChange. Saving the parent form through
 * the Common Routine validates + ingests the shadow blob automatically.
 *
 *   <UdcCustomFields
 *      entity="po_purchase_order"
 *      value={form.ext_attributes}
 *      onChange={(ext) => setForm(f => ({ ...f, ext_attributes: ext }))}
 *   />
 */

import { useMemo } from 'react';
import { DateInput } from './DateInput';
import { FilteredModalSingleSelect } from './FilteredModalSingleSelect';
import { useUdcFields } from '../../hooks/use_udc_fields';
import type { UdcField } from '../../hooks/use_udc_fields';

export interface UdcCustomFieldsProps {
  entity: string;
  /** Page the fields are placed on — scopes which fields render. */
  pageCode?: string;
  /** Active workspace; defaults to the ASF workspace context. */
  workspaceCode?: string | null;
  /** Optional filters so a screen can place a subset in a given tab/section. */
  tab?: string;
  section?: string;
  /** The parent form's ext_attributes object. */
  value: Record<string, unknown> | null | undefined;
  /** Receives the next ext_attributes object on any field change. */
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
  /** Pre-fetched fields (skip the internal hook fetch) — e.g. for a preview. */
  fields?: UdcField[];
  /** Hide section headings (when the host screen supplies its own). */
  hideSectionHeadings?: boolean;
}

export function UdcCustomFields({ entity, pageCode, workspaceCode, tab, section, value, onChange, disabled, fields: fieldsProp, hideSectionHeadings }: UdcCustomFieldsProps) {
  const hook = useUdcFields(entity, { workspaceCode, pageCode, enabled: fieldsProp === undefined });
  const all = fieldsProp ?? hook.fields;
  const cur = value ?? {};

  const shown = useMemo(() => all.filter(f =>
    (tab === undefined || (f.tab_code ?? '') === tab) &&
    (section === undefined || (f.section_code ?? '') === section)
  ), [all, tab, section]);

  // Group by section for headings, preserving display order.
  const groups = useMemo(() => {
    const m = new Map<string, UdcField[]>();
    for (const f of shown) { const key = f.section_code ?? ''; const arr = m.get(key) ?? []; arr.push(f); m.set(key, arr); }
    return Array.from(m.entries());
  }, [shown]);

  const set = (key: string, v: unknown) => onChange({ ...cur, [key]: v });

  if (fieldsProp === undefined && hook.loading) return <div className="text-csm-2xs text-csm-watermark">Loading custom fields…</div>;
  if (fieldsProp === undefined && hook.error) return <div className="text-csm-2xs text-csm-danger">Custom fields: {hook.error}</div>;
  if (shown.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {groups.map(([sec, fs]) => (
        <section key={sec || '_'}>
          {!hideSectionHeadings && sec && <h4 className="mb-1 text-csm-2xs font-semibold uppercase tracking-wide text-csm-ink-soft">{sec}</h4>}
          <div className="grid grid-cols-12 gap-x-3 gap-y-2">
            {fs.map(f => (
              <div key={f.key} style={{ gridColumn: `span ${Math.min(Math.max(f.col_span, 1), 12)} / span ${Math.min(Math.max(f.col_span, 1), 12)}` }}>
                <UdcControl field={f} value={cur[f.key]} onChange={v => set(f.key, v)} disabled={disabled} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Label({ field }: { field: UdcField }) {
  return (
    <label className="mb-0.5 block text-csm-2xs font-medium uppercase tracking-wide text-csm-ink-soft">
      {field.label}{field.required && <span className="ml-0.5 text-csm-danger">*</span>}
    </label>
  );
}

const INPUT = 'h-7 w-full rounded border border-csm-border px-1.5 text-csm-sm focus:border-primary focus:outline-none';

export function UdcControl({ field, value, onChange, disabled }: { field: UdcField; value: unknown; onChange: (v: unknown) => void; disabled?: boolean }) {
  const s = value == null ? '' : String(value);
  const help = field.help_text ? <p className="mt-0.5 text-csm-2xs text-csm-watermark">{field.help_text}</p> : null;

  switch (field.control) {
    case 'CHECKBOX':
      return (
        <label className="flex h-7 items-center gap-2 text-csm-sm">
          <input type="checkbox" checked={value === true || value === 'true' || value === 'Y'} disabled={disabled}
            onChange={e => onChange(e.target.checked)} />
          <span>{field.label}{field.required && <span className="ml-0.5 text-csm-danger">*</span>}</span>
        </label>
      );

    case 'TEXTAREA':
      return (
        <div><Label field={field} />
          <textarea value={s} disabled={disabled} placeholder={field.placeholder ?? undefined} rows={3}
            maxLength={field.max_len ?? undefined}
            onChange={e => onChange(e.target.value)} className="w-full rounded border border-csm-border px-1.5 py-1 text-csm-sm focus:border-primary focus:outline-none" />
          {help}
        </div>
      );

    case 'NUMBER':
      return (
        <div><Label field={field} />
          <input type="number" value={s} disabled={disabled} placeholder={field.placeholder ?? undefined}
            min={field.min_value ?? undefined} max={field.max_value ?? undefined}
            step={field.data_type === 'INT' ? 1 : 'any'}
            onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} className={INPUT} />
          {help}
        </div>
      );

    case 'DATE':
      return (
        <div><Label field={field} />
          <DateInput value={s ?? ''} disabled={disabled} onChange={v => onChange(v || null)} className={INPUT} />
          {help}
        </div>
      );

    case 'DATETIME':
      return (
        <div><Label field={field} />
          <input type="datetime-local" value={s} disabled={disabled} onChange={e => onChange(e.target.value || null)} className={INPUT} />
          {help}
        </div>
      );

    case 'RADIO':
      return (
        <div><Label field={field} />
          <div className="flex flex-wrap gap-3 py-1">
            {field.allowed_values.map(opt => (
              <label key={opt} className="flex items-center gap-1 text-csm-sm">
                <input type="radio" name={field.key} checked={s === opt} disabled={disabled} onChange={() => onChange(opt)} /> {opt}
              </label>
            ))}
          </div>
          {help}
        </div>
      );

    case 'SELECT':
    case 'MODAL_LOOKUP':
      return (
        <div>
          <FilteredModalSingleSelect
            label={field.label + (field.required ? ' *' : '')}
            options={field.allowed_values.map(v => ({ internal_code: v, display_text: v }))}
            selectedCode={s || null}
            onChange={code => onChange(code)}
            placeholder={field.placeholder ?? 'Select…'}
            disabled={disabled}
            allowClear={!field.required}
          />
          {help}
        </div>
      );

    case 'TEXT':
    default:
      return (
        <div><Label field={field} />
          <input type="text" value={s} disabled={disabled} placeholder={field.placeholder ?? undefined}
            maxLength={field.max_len ?? undefined}
            onChange={e => onChange(e.target.value)} className={INPUT} />
          {help}
        </div>
      );
  }
}
